import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { z } from 'zod';
import client from '../client';
import {
  annotationMutationMetaSchema,
  operationIdSchema,
  parseMutationFailure,
  publicLineItemSchema,
  type MutationResult,
} from './useLineItemPatch';
import { parseEnvelope } from '../envelope';

export class BatchUnavailableError extends Error {
  constructor() {
    super('Applying or undoing a batch has no M1 endpoint. No demonstration result has been substituted.');
    this.name = 'BatchUnavailableError';
  }
}

/** Legacy Inbox/Ask apply remains intentionally unavailable until its routes exist. */
export const applyBatchInputSchema = z.object({
  scope: z.enum(['similar_this_month', 'always_condition', 'single', 'ask_commit']),
  category_path: z.array(z.string()).optional(),
  business_use: z.enum(['none', 'mixed', 'primary']).optional(),
  transaction_ids: z.array(z.string()),
  condition_label: z.string().optional(),
}).strict();
export type ApplyBatchInput = z.infer<typeof applyBatchInputSchema>;
export const applyBatchResultSchema = z.object({
  batch_id: z.string(), applied_count: z.number(), applied_amount: z.string().nullable(), message: z.string(),
}).strict();
export type ApplyBatchResult = z.infer<typeof applyBatchResultSchema>;

export const auditBatchUndoRequestSchema = z.object({ operation_id: operationIdSchema }).strict();
export type AuditBatchUndoRequest = z.infer<typeof auditBatchUndoRequestSchema>;

export const auditBatchUndoDataSchema = z.object({
  undone_batch_id: z.string().uuid(),
  compensation_batch_id: z.string().uuid(),
  compensation_audit_ids: z.array(z.number().int().positive()),
  restored_count: z.number().int().nonnegative(),
  line_items: z.array(publicLineItemSchema),
}).strict();
export type AuditBatchUndoData = z.infer<typeof auditBatchUndoDataSchema>;
export const auditBatchUndoResponseSchema = z.object({
  data: auditBatchUndoDataSchema,
  meta: annotationMutationMetaSchema,
}).strict();

export type BatchUndoInput = string | { batchId: string; operationId?: string };

function newOperationId(): string {
  const generated = globalThis.crypto?.randomUUID?.();
  if (!generated) throw new Error('operation ID generation is unavailable');
  return operationIdSchema.parse(generated);
}

function undoRequest(input: BatchUndoInput): { batchId: string; body: AuditBatchUndoRequest } {
  if (typeof input === 'string') return { batchId: z.string().uuid().parse(input), body: { operation_id: newOperationId() } };
  return {
    batchId: z.string().uuid().parse(input.batchId),
    body: auditBatchUndoRequestSchema.parse({ operation_id: input.operationId ?? newOperationId() }),
  };
}

function parseUndoResponse(payload: unknown): MutationResult<AuditBatchUndoData> {
  const parsed = parseEnvelope(auditBatchUndoDataSchema, payload);
  const meta = annotationMutationMetaSchema.parse(parsed.meta ?? {});
  return meta.idempotent_replay
    ? { kind: 'replay', idempotent_replay: true, data: parsed.data }
    : { kind: 'success', idempotent_replay: false, data: parsed.data };
}

export function useBatchUndo() {
  const queryClient = useQueryClient();
  const apply = useMutation<ApplyBatchResult, BatchUnavailableError, ApplyBatchInput>({
    mutationFn: async () => { throw new BatchUnavailableError(); },
    retry: false,
  });

  const undo = useMutation<MutationResult<AuditBatchUndoData>, Error, BatchUndoInput>({
    mutationFn: async (input) => {
      const { batchId, body } = undoRequest(input);
      try {
        const response = await client.post(`/api/audit/batch/${batchId}/undo`, body);
        return parseUndoResponse(response.data);
      } catch (error) {
        if (isAxiosError(error)) return parseMutationFailure(error);
        throw error;
      }
    },
    retry: false,
    onSuccess: (result) => {
      if (result.kind === 'success' || result.kind === 'replay') {
        queryClient.invalidateQueries({ queryKey: ['transactions'] });
      }
    },
  });

  return { apply, undo };
}
